"use client";

import React from "react";
import { 
  Calendar, 
  User, 
  Clock, 
  ArrowRight, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Share2,
  Tag,
  ChevronRight
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardFooter, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="22358" className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-96 w-full animate-pulse rounded-xl bg-muted/50" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group overflow-hidden border-border/40 bg-card transition-all duration-300 hover:border-primary/40 hover:shadow-xl">
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden bg-muted">
              <img 
                src={post.featuredImage} 
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
          )}
          <CardHeader className="p-6 pb-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
              {post.categories?.[0]?.name || "Market News"}
            </div>
            <CardTitle className="line-clamp-2 text-xl font-bold text-foreground transition-colors group-hover:text-primary font-default">
              <Link to={post.link || "#"}>
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <CardDescription className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
              {post.excerpt ? (
                <div dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              ) : (
                "Deep dive into our latest property investment analysis and market forecasts for the UK social housing sector."
              )}
            </CardDescription>
          </CardContent>
          <CardFooter className="flex items-center justify-between border-t border-border/40 p-6">
            <span className="text-xs font-medium text-muted-foreground">{post.date}</span>
            <Link to={post.link || "#"}>
              <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 font-bold text-primary hover:bg-primary/5 hover:text-primary">
                Full Analysis <ArrowRight className="h-3 w-3" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPostId, categoryIds }: { currentPostId: string, categoryIds?: number[] }) {
  
  const queryArgs: any = {
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [Number(currentPostId)], 
    orderby: "date",
    order: "desc",
  };

  
  if (categoryIds && categoryIds.length > 0) {
    queryArgs.category__in = categoryIds;
  }

  const wp_query = new WP_Query(queryArgs);

  return (
    <section className="bg-muted/40 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground md:text-3xl font-default">
            Investment <span className="text-primary">Insights</span>
          </h2>
          <Link to="#">
            <Button variant="outline" className="hidden border-primary/20 bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground sm:flex">
              Explore All Insights
            </Button>
          </Link>
        </div>
        
        <PostsProvider wp_query={wp_query} className="w-full">
          <RelatedPostsConsumer />
        </PostsProvider>

        <div className="mt-8 flex justify-center sm:hidden">
          <Link to="#">
            <Button variant="outline" className="w-full border-primary/20 bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground">
              Explore All Insights
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-20">
        <div className="mb-8 h-12 w-3/4 animate-pulse rounded-lg bg-muted/50" />
        <div className="mb-12 h-96 w-full animate-pulse rounded-xl bg-muted/50" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-muted/50" />
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h1 className="mb-4 text-2xl font-bold text-foreground font-default">Post not found</h1>
        <p className="mb-8 text-muted-foreground">The investment analysis you are looking for is currently unavailable or has been archived.</p>
        <Link to="#">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90">Return to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const categoryIds = post.categories?.map(c => c.id) || [];

  return (
    <>
      <article className="bg-background pb-20 pt-12 md:pt-20">
        {}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {}
          <div className="mb-6 flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground">
            <Link to="#" className="transition-colors hover:text-primary">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="#" className="transition-colors hover:text-primary">Insights</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="font-bold text-primary">
              {post.categories?.[0]?.name || "Market Report"}
            </span>
          </div>

          {}
          <h1 className="mb-6 text-3xl font-bold leading-tight tracking-tight text-foreground md:text-4xl lg:text-5xl font-default">
            {post.title}
          </h1>

          {}
          <div className="mb-8 flex flex-wrap items-center gap-6 border-b border-border/60 pb-8 text-sm text-muted-foreground">
            {post.author && (
              <div className="flex items-center gap-2">
                <Avatar className="h-10 w-10 border border-primary/20">
                  <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
                  <AvatarFallback className="bg-secondary text-secondary-foreground"><User className="h-5 w-5" /></AvatarFallback>
                </Avatar>
                <span className="font-bold text-foreground">{post.author.name}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span>7 min read</span>
            </div>
          </div>
        </div>

        {}
        {post.featuredImage && (
          <div className="mx-auto mb-12 max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="relative aspect-[21/9] w-full overflow-hidden rounded-2xl shadow-2xl ring-1 ring-border/50">
              <img 
                src={post.featuredImage} 
                alt={post.title}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        )}

        {}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div 
            className="prose prose-lg prose-stone max-w-none text-foreground/90 font-default [&_a]:font-bold [&_a]:text-primary [&_a]:underline [&_a]:decoration-primary/30 [&_a]:underline-offset-4 hover:[&_a]:decoration-primary [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:bg-muted/50 [&_blockquote]:px-8 [&_blockquote]:py-4 [&_blockquote]:italic [&_blockquote]:text-foreground [&_h2]:mt-12 [&_h2]:font-default [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-foreground [&_h3]:mt-8 [&_h3]:font-default [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-foreground [&_img]:rounded-xl [&_img]:shadow-md [&_li]:marker:text-primary"
            dangerouslySetInnerHTML={{ __html: post.content || "" }} 
          />

          {}
          <div className="mt-12 flex flex-col gap-8 border-t border-border/60 pt-8 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="h-4 w-4 text-primary" />
              <span className="mr-2 text-sm font-bold text-foreground">Sectors:</span>
              {post.tags && post.tags.length > 0 ? (
                post.tags.map((tag) => (
                  <Link key={tag.id} to={tag.link || "#"}>
                    <span className="rounded-md bg-muted px-3 py-1 text-xs font-bold text-muted-foreground transition-all hover:bg-primary hover:text-primary-foreground">
                      {tag.name}
                    </span>
                  </Link>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">Investment Analysis</span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-foreground">Share Insights:</span>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" className="h-9 w-9 rounded-full border-border/60 hover:border-primary hover:bg-primary/5 hover:text-primary">
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-9 w-9 rounded-full border-border/60 hover:border-primary hover:bg-primary/5 hover:text-primary">
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-9 w-9 rounded-full border-border/60 hover:border-primary hover:bg-primary/5 hover:text-primary">
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-9 w-9 rounded-full border-border/60 hover:border-primary hover:bg-primary/5 hover:text-primary">
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {}
          {post.author && (
            <div className="mt-12 rounded-2xl bg-muted/40 p-10 border border-border/40">
              <div className="flex flex-col items-center gap-8 text-center sm:flex-row sm:text-left">
                <Avatar className="h-24 w-24 border-4 border-background shadow-md">
                  <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
                  <AvatarFallback className="bg-primary/10 text-primary text-2xl font-bold font-default">
                    {post.author.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-foreground font-default">Expert Profile: {post.author.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    Senior Investment Strategist specializing in government-backed yields and UK social housing compliance. Dedicated to delivering secure, asset-backed wealth growth for qualified investors.
                  </p>
                  <div className="mt-5 flex justify-center gap-4 sm:justify-start">
                    <Link to={post.author.link || "#"} className="text-sm font-bold text-primary transition-opacity hover:opacity-80">
                      View full portfolio by {post.author.name}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </article>

      {}
      <RelatedPostsSection currentPostId={post.id} categoryIds={categoryIds} />
    </>
  );
}

export default function SinglePostTemplate() {
  
  
  const currentWPQueryParams = (typeof window !== 'undefined' && (window as any).wvcClient) 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : {};
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <div id="single-post-template-section" className="min-h-screen bg-background text-foreground font-default">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </div>
  );
}